/**
 * Layer Drawer Utilities
 *
 * Helper functions for the Layer Drawer 2.0 system.
 */

import { Layer } from '../../../types/layer';
import { BaseLayer } from '../../layers2';

/**
 * Drawer height states
 */
export enum DrawerHeight {
  HIDDEN = 0,      // Completely hidden, show toggle icon instead
  COLLAPSED = 60,
  HALF = 0.4,      // 40% of viewport
  FULL = 0.9       // 90% of viewport
}

/**
 * Calculate actual height in pixels based on state
 */
export function calculateDrawerHeight(state: DrawerHeight, viewportHeight: number): number {
  if (state === DrawerHeight.HIDDEN) {
    return 0;
  }

  if (state === DrawerHeight.COLLAPSED) {
    return DrawerHeight.COLLAPSED;
  }

  return viewportHeight * state;
}

/**
 * Group layers by category
 */
export function groupLayersByCategory(layers: Layer[]): Map<string, Layer[]> {
  const groups = new Map<string, Layer[]>();

  layers.forEach(layer => {
    const category = layer.category || 'uncategorized';
    if (!groups.has(category)) {
      groups.set(category, []);
    }
    groups.get(category)!.push(layer);
  });

  return groups;
}

/**
 * Group layers by layer_group
 */
export function groupLayersByGroup(layers: Layer[]): Map<string, Layer[]> {
  const groups = new Map<string, Layer[]>();

  layers.forEach(layer => {
    const group = layer.layer_group || 'ungrouped';
    if (!groups.has(group)) {
      groups.set(group, []);
    }
    groups.get(group)!.push(layer);
  });

  return groups;
}

/**
 * Sort layers by order (descending - highest on top)
 */
export function sortLayersByOrder(layers: Layer[]): Layer[] {
  return [...layers].sort((a, b) => b.default_order - a.default_order);
}

/**
 * Filter layers by search term
 */
export function filterLayersBySearch(layers: Layer[], searchTerm: string): Layer[] {
  if (!searchTerm.trim()) {
    return layers;
  }

  const term = searchTerm.toLowerCase();

  return layers.filter(layer => {
    // Search in name
    if (layer.name.toLowerCase().includes(term)) {
      return true;
    }

    // Search in tags
    if (layer.tags.some(tag => tag.toLowerCase().includes(term))) {
      return true;
    }

    // Search in category
    if (layer.category.toLowerCase().includes(term)) {
      return true;
    }

    // Search in group
    if (layer.layer_group?.toLowerCase().includes(term)) {
      return true;
    }

    return false;
  });
}

/**
 * Get category display name
 */
export function getCategoryDisplayName(category: string): string {
  const names: Record<string, string> = {
    'backgrounds': 'Background Layers',
    'overlays': 'Overlay Layers',
    'live': 'Live Data Layers',
    'uncategorized': 'Other Layers'
  };

  return names[category] || category;
}

/**
 * Get layer type icon name for FontAwesome
 */
export function getLayerTypeIcon(type: string): string {
  const icons: Record<string, string> = {
    'wms': 'map',
    'wfs': 'map-marker-alt',
    'tms': 'map',
    'live': 'broadcast-tower',
    'geojson': 'chart-area'
  };

  return icons[type] || 'file';
}

/**
 * Clamp value between min and max
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * Debounce function
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;

  return (...args: Parameters<T>) => {
    if (timeout) {
      clearTimeout(timeout);
    }

    timeout = setTimeout(() => {
      func(...args);
    }, wait);
  };
}

/**
 * Format opacity percentage
 */
export function formatOpacity(opacity: number): string {
  return `${Math.round(opacity * 100)}%`;
}

/**
 * Check if layer matches filter criteria
 */
export function layerMatchesFilter(
  layer: Layer,
  filter: {
    category?: string;
    type?: string;
    group?: string;
    tags?: string[];
  }
): boolean {
  if (filter.category && layer.category !== filter.category) {
    return false;
  }

  if (filter.type && layer.type !== filter.type) {
    return false;
  }

  if (filter.group && layer.layer_group !== filter.group) {
    return false;
  }

  if (filter.tags && filter.tags.length > 0) {
    const hasAllTags = filter.tags.every(tag => layer.tags.includes(tag));
    if (!hasAllTags) {
      return false;
    }
  }

  return true;
}

/**
 * Sync database layer with BaseLayer instance state
 */
export function syncLayerState(dbLayer: Layer, instance: BaseLayer): Layer {
  return {
    ...dbLayer,
    enabled: instance.isEnabled(),
    default_opacity: instance.getOpacity(),
    default_order: instance.getOrder()
  };
}
